import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOT_OPTIONS = ["Lot S1", "Lot S3A1", "Lot S4A", "Lot S5A", "Lot S6A", "Lot S7A", "Lot S8A", "General Information"];

const inputCls =
  "bg-transparent border-cream/25 text-cream placeholder:text-cream/40 rounded-sm focus-visible:ring-1 focus-visible:ring-brass focus-visible:ring-offset-0 h-12";

export const ContactForm = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    lot_of_interest: "",
    preferred_contact: "",
    working_with_agent: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onSelect = (e) => setForm((f) => ({ ...f, lot_of_interest: e.detail }));
    window.addEventListener("select-lot", onSelect);
    return () => window.removeEventListener("select-lot", onSelect);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Thank you for your interest in building in Woodlake Estates. Brandy will contact you soon.");
    } catch {
      toast.error("Something went wrong sending your request. Please call Brandy at 504-329-6166.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="inquiry-confirmation" className="bg-cream/5 border border-brass/40 p-12 lg:p-16 text-center flex flex-col items-center gap-5">
        <CheckCircle2 size={44} className="text-brass" />
        <h3 className="font-serif text-3xl text-cream">Request Received</h3>
        <p className="font-sans text-base text-cream/75 max-w-md">
          Thank you for your interest in building in Woodlake Estates. Brandy will contact you soon.
        </p>
        <button
          data-testid="send-another-inquiry-btn"
          onClick={() => setDone(false)}
          className="mt-2 font-sans text-sm text-brass underline underline-offset-4 hover:text-[#cbaa66] transition-colors duration-300"
        >
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form id="inquiry-form" data-testid="inquiry-form" onSubmit={submit} className="bg-cream/5 border border-cream/15 p-8 lg:p-12">
      <h3 className="font-serif text-3xl text-cream">Request More Information</h3>
      <p className="mt-3 mb-8 font-sans text-sm leading-relaxed text-cream/65">
        Complete the form below and Brandy will follow up with additional information about the property or lot you
        are interested in.
      </p>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-2">
          <Label htmlFor="first-name" className="text-cream/80 font-sans text-xs tracking-[0.15em] uppercase">First Name</Label>
          <Input id="first-name" data-testid="first-name-input" required value={form.first_name} onChange={set("first_name")} className={inputCls} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="last-name" className="text-cream/80 font-sans text-xs tracking-[0.15em] uppercase">Last Name</Label>
          <Input id="last-name" data-testid="last-name-input" required value={form.last_name} onChange={set("last_name")} className={inputCls} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="email" className="text-cream/80 font-sans text-xs tracking-[0.15em] uppercase">Email Address</Label>
          <Input id="email" data-testid="email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="phone" className="text-cream/80 font-sans text-xs tracking-[0.15em] uppercase">Phone Number</Label>
          <Input id="phone" data-testid="phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} />
        </div>
        <div className="flex flex-col gap-2">
          <Label className="text-cream/80 font-sans text-xs tracking-[0.15em] uppercase">Lot of Interest</Label>
          <Select required value={form.lot_of_interest} onValueChange={(v) => setForm((f) => ({ ...f, lot_of_interest: v }))}>
            <SelectTrigger data-testid="lot-select" className={`${inputCls} [&>span]:text-left`}>
              <SelectValue placeholder="Select a lot" />
            </SelectTrigger>
            <SelectContent className="bg-forest border-cream/20 text-cream rounded-sm">
              {LOT_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} data-testid={`lot-option-${o.toLowerCase().replace(/\s+/g, "-")}`} className="focus:bg-cream/10 focus:text-cream">
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2">
          <Label className="text-cream/80 font-sans text-xs tracking-[0.15em] uppercase">Preferred Contact Method</Label>
          <Select required value={form.preferred_contact} onValueChange={(v) => setForm((f) => ({ ...f, preferred_contact: v }))}>
            <SelectTrigger data-testid="contact-method-select" className={inputCls}>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent className="bg-forest border-cream/20 text-cream rounded-sm">
              <SelectItem value="Email" data-testid="contact-method-email" className="focus:bg-cream/10 focus:text-cream">Email</SelectItem>
              <SelectItem value="Phone" data-testid="contact-method-phone" className="focus:bg-cream/10 focus:text-cream">Phone</SelectItem>
              <SelectItem value="Text Message" data-testid="contact-method-text" className="focus:bg-cream/10 focus:text-cream">Text Message</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2 sm:col-span-2">
          <Label className="text-cream/80 font-sans text-xs tracking-[0.15em] uppercase">Are You Working With a Real Estate Agent?</Label>
          <Select value={form.working_with_agent} onValueChange={(v) => setForm((f) => ({ ...f, working_with_agent: v }))}>
            <SelectTrigger data-testid="agent-select" className={inputCls}>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent className="bg-forest border-cream/20 text-cream rounded-sm">
              <SelectItem value="Yes" data-testid="agent-yes" className="focus:bg-cream/10 focus:text-cream">Yes</SelectItem>
              <SelectItem value="No" data-testid="agent-no" className="focus:bg-cream/10 focus:text-cream">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2 sm:col-span-2">
          <Label htmlFor="message" className="text-cream/80 font-sans text-xs tracking-[0.15em] uppercase">Message</Label>
          <Textarea id="message" data-testid="message-input" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} h-auto`} />
        </div>
      </div>

      <button
        data-testid="inquiry-submit-btn"
        type="submit"
        disabled={sending}
        className="mt-8 w-full bg-brass text-forest font-sans text-sm font-semibold tracking-[0.15em] uppercase px-8 py-4 rounded-sm transition-colors duration-300 hover:bg-[#cbaa66] disabled:opacity-60"
      >
        {sending ? "Sending…" : "Request Information"}
      </button>
    </form>
  );
};
